/**
 * The bridge's stand-in body for a WhatsApp attachment sent without a caption, and the rule that hides it where the CSP
 * draws the attachment itself (CT-15747; O6, a real-phone check of 2026-10-30: an inbound voice note drew
 * "(ptt: audio/mpeg)" as a text line above its player):
 *
 *   "(<Periskope message_type>: <the attachment's MIME type>)"
 *
 * e.g. "(ptt: audio/ogg; codecs=opus)" for a voice note, "(audio: audio/mpeg)", "(image: image/jpeg)",
 * "(video: video/mp4)", "(document: application/pdf)", "(sticker: image/webp)": one form for every type Periskope sends
 * with media, whatever the type's name.
 *
 * ONE RULE WITH CS-ANALYSIS. This file is a port, function for function, of infurnia-cs-analysis
 * packages/shared/src/comms/media-placeholder.ts (read at origin/nikhil 98b2791f), with the CSP's snake_case names:
 *   waMediaPlaceholder -> wa_media_placeholder, waMediaPlaceholderMimetype -> wa_media_placeholder_mimetype,
 *   isWaMediaPlaceholder -> is_wa_media_placeholder, shownMessageBody -> shown_message_body,
 *   isWaMediaPlaceholderOfKind -> is_wa_media_placeholder_of_kind, placeholderKind -> placeholder_kind;
 * the same regular expression, the same conditions. Its spec's cases (packages/comms-client media-placeholder.spec.ts)
 * are ported in comms_media_placeholder.test.js. A change to the rule is a change to both files.
 *
 * The STORED body keeps the stand-in: the digest, the escalation alerter, the corpus and the knowledge miner read the
 * body as text, and there it is the only sign that the message carried an attachment. Nothing the CSP stores, sends or
 * edits reads this rule: it decides only the text a screen that draws the attachment draws for the message. It hides a
 * stand-in OF AN ATTACHMENT THE MESSAGE CARRIES, and nothing else: the whole body (its ends trimmed) is
 * "(<word>: <MIME type>)" and that MIME type is, exactly, one of the message's own attachments'. A caption is never a
 * stand-in (the bridge writes one only for an empty body), and a text that reads like one (on a message with no
 * attachment, or with an attachment of another type) is drawn as it is.
 *
 * Where the CSP reads it: the thread (StoreDashboard/ClientHealthTab/CommsThread.jsx: the bubble, the quote line of a
 * reply, the replying-to bar, the missed-escalation bar) through shown_message_body; the Chat inbox's last-message line
 * (views/Chat/index.jsx) through is_wa_media_placeholder_of_kind, as the feed row carries only the kind of the last
 * message's first attachment (lastMediaKind). The browser notification (public/push-sw.js) draws the text cs-analysis
 * puts in the push payload, which applies this rule itself (worker comms-push-pump/push-notice.ts) and carries no
 * attachments to apply it to here. The knowledge-save dialog quotes the stored body: it draws no attachment, so there
 * the stand-in is the one sign of it.
 *
 * No React and no import: a pure module, tested on its own.
 */

/**
 * What the bridge stores as the body of a WhatsApp message with an attachment of `mimetype` and no caption. A message
 * with no type is "message", as the webhook has always written it.
 */
export function wa_media_placeholder(message_type, mimetype) {
    return `(${message_type == null ? 'message' : message_type}: ${mimetype})`;
}

/** "(<word>: <MIME type>)": the word has no space, colon or bracket; the MIME type runs to the closing bracket. */
export const WA_MEDIA_PLACEHOLDER = /^\(([^\s():]+): ([^\n]+)\)$/;

/** The MIME type a stand-in names ("(ptt: audio/mpeg)" -> "audio/mpeg"); null when the text is not one. */
export function wa_media_placeholder_mimetype(body) {
    const m = WA_MEDIA_PLACEHOLDER.exec(typeof body === 'string' ? body.trim() : '');
    return m ? m[2] : null;
}

/** Whether `body` is the stand-in of one of the message's own attachments (`media`): the rule in the header. */
export function is_wa_media_placeholder(body, media) {
    const mimetype = wa_media_placeholder_mimetype(body);
    if (mimetype === null || !Array.isArray(media)) return false;
    return media.some((r) => r !== null && typeof r === 'object' && r.mimetype === mimetype);
}

/**
 * The text a screen that draws a message's attachments draws for it: '' when the body is only the stand-in of one of
 * them, else the body as stored ('' for none).
 */
export function shown_message_body(m) {
    const body = m && typeof m.body === 'string' ? m.body : '';
    return is_wa_media_placeholder(body, m ? m.mediaRefs : null) ? '' : body;
}

/**
 * The same rule where only the KIND of the message's first attachment is known (the Chat inbox's feed rows: the last
 * message's text and `lastMediaKind`, cs-analysis apps/api comms/feed-media-kind.ts): the stand-in's MIME type is of
 * that kind. A WhatsApp message carries one attachment, the one its stand-in names. No kind (no attachment, a deleted
 * message) or one this rule does not name: never a stand-in.
 */
export function is_wa_media_placeholder_of_kind(body, kind) {
    const mimetype = wa_media_placeholder_mimetype(body);
    return mimetype !== null && kind != null && placeholder_kind(mimetype) === kind;
}

/** The feed's split (commsMediaKind): the top-level type, lower case, parameters dropped; anything else is a document. */
export function placeholder_kind(mimetype) {
    const m = mimetype.split(';')[0].trim().toLowerCase();
    if (m.startsWith('image/')) return 'image';
    if (m.startsWith('video/')) return 'video';
    if (m.startsWith('audio/')) return 'audio';
    return 'document';
}
